import { z } from 'zod';

const decimalString = z.string().regex(/^-?\d+(\.\d{1,4})?$/);
const positiveDecimalString = z.string().regex(/^\d+(\.\d{1,4})?$/);

/** Order-level discount on a sales document (header discount; coupon-driven later). */
export const DocumentDiscountTypeSchema = z.enum(['none', 'percent', 'fixed']);
export type DocumentDiscountType = z.infer<typeof DocumentDiscountTypeSchema>;

// ---- Document line (shared) ----
export const DocumentLineInputSchema = z.object({
  itemId: z.string().uuid().optional(),
  // Packaging unit (item_units row) sold on this line. unitName + unitFactor
  // are display/print snapshots; docs post GL only (no stock) so the factor
  // doesn't convert anything here — it's recorded for parity with POS.
  itemUnitId: z.string().uuid().optional(),
  unitName: z.string().max(64).optional(),
  unitFactor: positiveDecimalString.optional(),
  accountId: z.string().uuid().optional(),
  description: z.string().max(500).optional(),
  quantity: positiveDecimalString.default('1'),
  unitPrice: positiveDecimalString.default('0'),
  taxRate: positiveDecimalString.default('0'),
});
export type DocumentLineInput = z.infer<typeof DocumentLineInputSchema>;

export const DocumentLineSchema = z.object({
  id: z.string().uuid(),
  docId: z.string().uuid(),
  lineNo: z.number().int(),
  itemId: z.string().uuid().nullable(),
  itemUnitId: z.string().uuid().nullable(),
  unitName: z.string().nullable(),
  unitFactor: decimalString,
  accountId: z.string().uuid().nullable(),
  description: z.string().nullable(),
  quantity: decimalString,
  unitPrice: decimalString,
  taxRate: decimalString,
  taxAmount: decimalString,
  lineTotal: decimalString,
});
export type DocumentLine = z.infer<typeof DocumentLineSchema>;

// ---- Sales Orders (Module 7) ----
export const SalesOrderStatusSchema = z.enum(['draft', 'submitted', 'fulfilled', 'cancelled']);
export type SalesOrderStatus = z.infer<typeof SalesOrderStatusSchema>;

export const SalesOrderInputSchema = z.object({
  docDate: z.string(),
  partyId: z.string().uuid(),
  currencyCode: z.string().length(3).regex(/^[A-Z]{3}$/),
  notes: z.string().max(2000).optional(),
  // Order-level discount (applied to subtotal; tax is on pre-discount lines).
  discountType: DocumentDiscountTypeSchema.optional(),
  discountValue: positiveDecimalString.optional(),
  // A coupon code (sales side) drives the discount and overrides the manual one.
  couponCode: z.string().max(64).optional(),
  /**
   * The warehouse this document names: goods SHIP from it (SO/invoice) or
   * arrive INTO it (PO/bill, via the extends below). Absent = ship-time
   * inference / tenant default — exactly what every pre-column document meant.
   */
  warehouseId: z.string().uuid().optional(),
  lines: z.array(DocumentLineInputSchema).min(1),
});
export type SalesOrderInput = z.infer<typeof SalesOrderInputSchema>;

export const SalesOrderSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  docNumber: z.string(),
  docDate: z.string(),
  partyId: z.string().uuid(),
  status: SalesOrderStatusSchema,
  currencyCode: z.string().length(3),
  subtotal: decimalString,
  taxTotal: decimalString,
  total: decimalString,
  discountType: DocumentDiscountTypeSchema,
  discountValue: decimalString,
  discountTotal: decimalString,
  couponId: z.string().uuid().nullable(),
  couponCode: z.string().nullable(),
  warehouseId: z.string().uuid().nullable(),
  notes: z.string().nullable(),
  lines: z.array(DocumentLineSchema),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type SalesOrder = z.infer<typeof SalesOrderSchema>;

// ---- Invoices (Module 8) ----
export const InvoiceStatusSchema = z.enum(['draft', 'issued', 'paid', 'cancelled']);
export type InvoiceStatus = z.infer<typeof InvoiceStatusSchema>;

export const InvoiceInputSchema = SalesOrderInputSchema.extend({
  dueDate: z.string().optional(),
});
export type InvoiceInput = z.infer<typeof InvoiceInputSchema>;

export const InvoiceSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  docNumber: z.string(),
  docDate: z.string(),
  dueDate: z.string().nullable(),
  partyId: z.string().uuid(),
  status: InvoiceStatusSchema,
  currencyCode: z.string().length(3),
  subtotal: decimalString,
  taxTotal: decimalString,
  total: decimalString,
  discountType: DocumentDiscountTypeSchema,
  discountValue: decimalString,
  discountTotal: decimalString,
  couponId: z.string().uuid().nullable(),
  couponCode: z.string().nullable(),
  amountPaid: decimalString,
  postedJournalEntryId: z.string().uuid().nullable(),
  warehouseId: z.string().uuid().nullable(),
  notes: z.string().nullable(),
  lines: z.array(DocumentLineSchema),
  postedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type Invoice = z.infer<typeof InvoiceSchema>;

// ---- Purchase Orders (Module 9) ----
export const PurchaseOrderStatusSchema = z.enum(['draft', 'submitted', 'received', 'cancelled']);
export type PurchaseOrderStatus = z.infer<typeof PurchaseOrderStatusSchema>;

// warehouseId inherited: on the purchase side it is the DESTINATION, carried
// into the bill on conversion.
export const PurchaseOrderInputSchema = SalesOrderInputSchema;
export type PurchaseOrderInput = z.infer<typeof PurchaseOrderInputSchema>;

export const PurchaseOrderSchema = SalesOrderSchema.extend({
  status: PurchaseOrderStatusSchema,
});
export type PurchaseOrder = z.infer<typeof PurchaseOrderSchema>;

// ---- Bills (Module 10) ----
export const BillStatusSchema = z.enum(['draft', 'approved', 'paid', 'cancelled']);
export type BillStatus = z.infer<typeof BillStatusSchema>;

// warehouseId inherited from the invoice shapes — RECEIVES on a bill, SHIPS on
// an invoice, same column either way.
export const BillInputSchema = InvoiceInputSchema.extend({
  /** The SUPPLIER's own invoice number, typed off their paper. */
  supplierInvoiceNo: z.string().max(64).optional(),
});
export type BillInput = z.infer<typeof BillInputSchema>;

export const BillSchema = InvoiceSchema.extend({
  status: BillStatusSchema,
  supplierInvoiceNo: z.string().nullable().optional(),
});
export type Bill = z.infer<typeof BillSchema>;

// ---- Payments (Module 11) ----
export const PaymentDirectionSchema = z.enum(['inbound', 'outbound']);
export type PaymentDirection = z.infer<typeof PaymentDirectionSchema>;

export const PaymentStatusSchema = z.enum(['draft', 'posted', 'voided']);
export type PaymentStatus = z.infer<typeof PaymentStatusSchema>;

export const PaymentAllocationSchema = z.object({
  targetType: z.enum(['invoice', 'bill']),
  targetId: z.string().uuid(),
  amount: positiveDecimalString,
});
export type PaymentAllocation = z.infer<typeof PaymentAllocationSchema>;

export const PaymentInputSchema = z.object({
  paymentDate: z.string(),
  direction: PaymentDirectionSchema,
  partyId: z.string().uuid(),
  currencyCode: z.string().length(3).regex(/^[A-Z]{3}$/),
  amount: positiveDecimalString,
  cashAccountId: z.string().uuid().optional(),
  method: z.string().max(32).optional(),
  reference: z.string().max(64).optional(),
  notes: z.string().max(2000).optional(),
  allocations: z.array(PaymentAllocationSchema).optional(),
});
export type PaymentInput = z.infer<typeof PaymentInputSchema>;

export const PaymentSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  docNumber: z.string(),
  paymentDate: z.string(),
  direction: PaymentDirectionSchema,
  partyId: z.string().uuid(),
  status: PaymentStatusSchema,
  currencyCode: z.string().length(3),
  amount: decimalString,
  cashAccountId: z.string().uuid().nullable(),
  method: z.string().nullable(),
  reference: z.string().nullable(),
  postedJournalEntryId: z.string().uuid().nullable(),
  notes: z.string().nullable(),
  postedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type Payment = z.infer<typeof PaymentSchema>;
